// src/pages/defeat.jsx
import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import './defeat.css';
import { useSound } from '../hooks/useSound';

const MOTIVATIONAL = [
  "Every master was once a beginner. Try again!",
  "The prisms aren't going to sort themselves... yet. 💪",
  "Almost! The pattern is within your reach.",
  "Shake it off and come back stronger!",
  "A true Prism Pal never gives up!",
];

const Defeat = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { playHover, playClick } = useSound();

  const {
    level     = 1,
    replayPath = `/level${level}`,
  } = location.state || {};

  const quote = MOTIVATIONAL[level % MOTIVATIONAL.length];

  const cardRef    = useRef(null);
  const charRef    = useRef(null);
  const titleRef   = useRef(null);
  const quoteRef   = useRef(null);
  const btnsRef    = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo(cardRef.current,
      { opacity: 0, y: 50, scale: 0.88 },
      { opacity: 1, y: 0,  scale: 1,   duration: 0.7, ease: 'back.out(1.3)' }
    );
    tl.fromTo(charRef.current,
      { opacity: 0, scale: 0.5, rotate: -20 },
      { opacity: 1, scale: 1,   rotate:   0, duration: 0.6, ease: 'back.out(2)' },
      '-=0.3'
    );
    tl.fromTo(titleRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0,  duration: 0.5 },
      '-=0.15'
    );
    tl.fromTo(quoteRef.current,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0,  duration: 0.45 },
      '-=0.1'
    );
    tl.fromTo(btnsRef.current,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0,  duration: 0.45 },
      '-=0.05'
    );

    /* Idle wobble on character */
    gsap.to(charRef.current, {
      y: -10, duration: 1.6, yoyo: true, repeat: -1, ease: 'sine.inOut', delay: 0.8
    });

    return () => tl.kill();
  }, []);

  const handleReplay = useCallback(() => { playClick(); navigate(replayPath); }, [navigate, playClick, replayPath]);
  const handleLevels = useCallback(() => { playClick(); navigate('/levels'); },   [navigate, playClick]);

  return (
    <main className="defeat">
      {/* Background particles */}
      <div className="defeat-particles" aria-hidden="true">
        {Array.from({ length: 18 }).map((_, i) => (
          <span key={i} className="defeat-particle" style={{
            left:            `${Math.random() * 100}%`,
            animationDelay:  `${Math.random() * 3}s`,
            animationDuration:`${2 + Math.random() * 2}s`,
            width:  6 + Math.random() * 6,
            height: 6 + Math.random() * 6,
          }} />
        ))}
      </div>

      <section className="defeat-card" ref={cardRef}>

        {/* Animated character */}
        <div className="defeat-char-wrap" ref={charRef}>
          <div className="defeat-char" />
          <div className="defeat-char-glow" />
        </div>

        {/* Title */}
        <div ref={titleRef}>
          <h1 className="defeat-title">LEVEL FAILED</h1>
          <h4 className="defeat-sub">Level {level} — Better luck next time!</h4>
        </div>

        {/* Motivational quote */}
        <div className="defeat-quote-wrap" ref={quoteRef}>
          <div className="defeat-char-name">PRISMA SAYS:</div>
          <p className="defeat-quote">"{quote}"</p>
        </div>

        {/* Buttons */}
        <div className="defeat-btn-row" ref={btnsRef}>
          <button
            className="defeat-btn defeat-btn--solid"
            onClick={handleReplay}
            onMouseEnter={playHover}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            TRY AGAIN
          </button>

          <button
            className="defeat-btn defeat-btn--ghost"
            onClick={handleLevels}
            onMouseEnter={playHover}
          >
            BACK TO LEVELS
          </button>
        </div>

        <div className="defeat-card-glow" />
      </section>
    </main>
  );
};

export default Defeat;